// Internal
import { DayStatus, IAttendance } from "@/types/attendance-sheet.types";
import { parseDayMonthYearString } from "@/utils/dates";
import { Checkmark, Terminated, X } from "@/components/ui/Icons";

// Antd
import { Calendar } from "antd";

// MISC
import dayjs from "dayjs";

export default function ReadOnlyView({
  attendanceSheet,
  noCareBillingPeriod = false,
}: {
  attendanceSheet: IAttendance | null;
  noCareBillingPeriod?: boolean;
}) {
  // Search params
  const startDate = attendanceSheet?.periodStartDate;
  const endDate = attendanceSheet?.periodEndDate;
  const periodStart = parseDayMonthYearString(startDate);
  const periodEnd = parseDayMonthYearString(endDate);

  return (
    <div>
      <EasCalendarLegend noCareBillingPeriod={noCareBillingPeriod} />

      <Calendar
        className="calendar__read-only"
        fullCellRender={(date) => {
          const _Content = () => {
            if (noCareBillingPeriod) {
              const isInBillingPeriod =
                !date.isBefore(periodStart, "day") &&
                !date.isAfter(periodEnd, "day");

              if (!isInBillingPeriod) {
                return false;
              }

              return (
                <div className="flex justify-end lg:justify-between">
                  <span className="w-[60%] hidden lg:table">Not Attended</span>

                  <span className="text-[17px] lg:text-[16px]">
                    <X />
                  </span>
                </div>
              );
            }

            const _day = attendanceSheet?.days?.find(
              (day) => day.dateAttended === date.format("YYYY-MM-DD")
            );

            if (
              !_day ||
              (attendanceSheet?.terminationDate &&
                date.isAfter(dayjs(attendanceSheet?.terminationDate)))
            ) {
              return false;
            }

            if (
              attendanceSheet?.terminationDate &&
              date.isSame(dayjs(attendanceSheet?.terminationDate))
            ) {
              return (
                <div className="flex justify-end lg:justify-between">
                  <span className="w-[60%] hidden lg:table font-semibold">
                    Terminated
                  </span>

                  <span className="text-[17px] lg:text-[16px]">
                    <Terminated />
                  </span>
                </div>
              );
            }

            if (_day?.code === DayStatus.NO_CARE_PROVIDED) {
              return (
                <div className="flex justify-end lg:justify-between">
                  <span className="w-[60%] hidden lg:table">
                    No Care Provided
                  </span>

                  <span className="text-[17px] lg:text-[16px]">
                    <X />
                  </span>
                </div>
              );
            }

            return (
              <div className="flex justify-end lg:justify-between">
                <span className="hidden lg:table">Attended</span>

                <span className="text-[17px] lg:text-[16px]">
                  <Checkmark />
                </span>
              </div>
            );
          };

          return (
            <div className="ant-picker-cell-inner ant-picker-calendar-date">
              <div className="ant-picker-calendar-date-value">
                {date.date()}
              </div>

              <div className="ant-picker-calendar-date-content">
                <_Content />
              </div>
            </div>
          );
        }}
        disabledDate={(date) => {
          if (noCareBillingPeriod) {
            return (
              date.isBefore(periodStart.startOf("month"), "day") ||
              date.isAfter(periodEnd.endOf("month"), "day")
            );
          }

          return (
            !attendanceSheet?.days?.find(
              (day) => day.dateAttended === date.format("YYYY-MM-DD")
            ) ||
            Boolean(
              attendanceSheet?.terminationDate &&
                date.isAfter(dayjs(attendanceSheet?.terminationDate))
            )
          );
        }}
        headerRender={() => null}
        validRange={
          noCareBillingPeriod
            ? [periodStart.startOf("month"), periodEnd.endOf("month")]
            : [periodStart, periodEnd]
        }
        defaultValue={periodStart}
        style={{ maxWidth: "1200px" }}
      />
    </div>
  );
}

function EasCalendarLegend({
  noCareBillingPeriod = false,
}: {
  noCareBillingPeriod?: boolean;
}) {
  return (
    <div className="mb-8 block lg:hidden">
      <p className="font-semibold">Legend</p>

      <div className="block lg:table">
        <div className="flex flex-col md:flex-row p-3 mt-2 bg-gray-200 gap-6 rounded-lg">
          {noCareBillingPeriod ? (
            <div className="flex gap-2 items-center">
              <div className="w-7 h-7 rounded-md bg-white relative shadow-sm">
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[18px] font-semibold">
                  <X className="mb-[-2px]" />
                </span>
              </div>

              <span className="font-md">Not Attended</span>
            </div>
          ) : (
            <>
              <div className="flex gap-2 items-center">
                <div className="w-7 h-7 rounded-md bg-white relative shadow-sm">
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[18px] font-semibold">
                    <Checkmark className="mb-[-2px]" />
                  </span>
                </div>

                <span className="font-md">Attended</span>
              </div>

              <div className="flex gap-2 items-center">
                <div className="w-7 h-7 rounded-md bg-white relative shadow-sm">
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[18px] font-semibold">
                    <X className="mb-[-2px]" />
                  </span>
                </div>

                <span className="font-md">No Care Provided</span>
              </div>

              <div className="flex gap-2 items-center">
                <div className="w-7 h-7 rounded-md bg-white relative shadow-sm">
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[18px] font-semibold">
                    <Terminated className="mb-[-2px]" />
                  </span>
                </div>

                <span className="font-md">Termination Date</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
